
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router";
import { RegistrationProvider } from "@/contexts/RegistrationContext";
import ImpersonationReturnButton from "@/components/ImpersonationReturnButton";
import PartnerLegalGate from "@/components/PartnerLegalGate";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

// Lazy-loaded pages (code splitting por rota)
const Index = lazy(() => import("./pages/Index"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const RetreatDetails = lazy(() => import("./pages/RetreatDetails"));
const RetreatListings = lazy(() => import("./pages/RetreatListings"));
const VenueDetails = lazy(() => import("./pages/VenueDetails"));
const VenueListings = lazy(() => import("./pages/VenueListings"));
const PartnerSpaces = lazy(() => import("./pages/PartnerSpaces"));
const Partner = lazy(() => import("./pages/Partner"));
const BecomePartner = lazy(() => import("./pages/BecomePartner"));
const RetreatRegistration = lazy(() => import("./pages/RetreatRegistration"));
const VenueRegistration = lazy(() => import("./pages/VenueRegistration"));
const Reservation = lazy(() => import("./pages/Reservation"));
const Profile = lazy(() => import("./pages/Profile"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const Admin = lazy(() => import("./pages/Admin"));
const Finance = lazy(() => import("./pages/Finance"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostDetails = lazy(() => import("./pages/BlogPostDetails"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const ParticipantInterest = lazy(() => import("./pages/ParticipantInterest"));
const CancellationPolicy = lazy(() => import("./pages/CancellationPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <RegistrationProvider>
      <BrowserRouter>
        <TooltipProvider>
          <AnalyticsTracker />
          <Suspense fallback={
            <div className="flex min-h-screen items-center justify-center bg-white">
              <div className="flex items-center gap-3 rounded-full border border-[#FBAB97]/60 bg-[#F3F7F4] px-5 py-3 text-sm font-semibold text-[#215834]">
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#F87C5D]" />
                Carregando...
              </div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/sobre-nos" element={<AboutUs />} />
              <Route path="/central-de-ajuda" element={<HelpCenter />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/retiros" element={<RetreatListings />} />
              <Route path="/retiros/:id" element={<RetreatDetails />} />
              <Route path="/locais" element={<VenueListings />} />
              <Route path="/locais/:id" element={<VenueDetails />} />
              <Route path="/espacos-parceiros" element={<PartnerSpaces />} />
              <Route path="/torne-se-um-parceiro" element={<BecomePartner />} />
              <Route path="/cadastrar-retiro" element={<RetreatRegistration />} />
              <Route path="/cadastrar-local" element={<VenueRegistration />} />
              <Route path="/reserva/:type/:id" element={<Reservation />} />
              <Route path="/interesse/:type/:id" element={<ParticipantInterest />} />
              <Route path="/perfil" element={<Profile />} />
              <Route path="/politica-de-cancelamento" element={<CancellationPolicy />} />
              <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
              <Route path="/configuracoes" element={<AccountSettings />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/parceiro" element={<Partner />} />
              <Route path="/financeiro" element={<Finance />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPostDetails />} />
              <Route path="/admin/blog" element={<AdminBlog />} />
              <Route path="/em-breve" element={<ComingSoon />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <FloatingWhatsApp />
          <PartnerLegalGate />
          <ImpersonationReturnButton />
          <Toaster />
          <Sonner />
        </TooltipProvider>
      </BrowserRouter>
    </RegistrationProvider>
  </QueryClientProvider>
);

export default App;
